import { DescriptionCaption } from '@/components/Description';
import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { ListViewLabel, ListViewRequiredLabel } from '@/components/ListView';
import { useTransactionItemDetailEdit } from '@/features/transaction_items/Detail/context/TransactionItemDetailEdit';
import { MAX_CUSTOM_TRANSACTION_ITEMS_COUNT } from '@/features/transaction_items/List/Table/TableAction/TableAction';
import { useSubmitUpdatePullDownTransactionItem } from '@/features/transaction_items/components/Form/hooks/useSubmitUpdatePullDownTransactionItem';
import { useTranslation } from '@/i18n';
import { maxLengthWithSurrogate } from '@/libs/validation/maxLengthWithSurrogate';
import {
  Bottom,
  ButtonV2,
  Form,
  FormCheckbox,
  FormInput,
  ListView,
  Top,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ErrorOption,
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import styles from './Top.module.scss';

const cx = classNames.bind(styles);

export type EditFormProps = {
  label: string;
  visibility: boolean;
  is_required?: boolean;
  tooltip_message?: string;
  isActiveCustomItemCountLimitEnabled: boolean;
  isFlexibleValidationEnabled: boolean;
  visibleCustomItemsInUseCount?: number;
};

type TransactionItemDetailForm = {
  label: string;
  visibility: boolean;
  is_required: boolean;
  tooltip_message: string;
};

const leftRightRatio = {
  left: {
    max: '160px',
    min: '160px',
  },
  right: {},
};

export const EditForm: FC<EditFormProps> = memo(
  ({
    label,
    visibility,
    is_required,
    tooltip_message,
    isActiveCustomItemCountLimitEnabled,
    isFlexibleValidationEnabled,
    visibleCustomItemsInUseCount,
  }) => {
    const { t } = useTranslation();
    const notify = useToastAPI();
    const { onView } = useTransactionItemDetailEdit();
    const [
      { setError: setVisibleLimitError },
      visibleLimitErrorContextHolder,
      VisibleLimitErrorProvider,
      clearVisibleLimitErrorMessage,
    ] = useErrorInlineNotification();
    const [isLoading, setIsLoading] = useState(false);
    const [visibleCountServerErrorMessage, setVisibleCountServerErrorMessage] =
      useState<string | null>(null);

    const clearVisibleLimitBanner = useCallback(() => {
      setVisibleCountServerErrorMessage(null);
      clearVisibleLimitErrorMessage();
    }, [clearVisibleLimitErrorMessage]);

    useEffect(() => {
      if (!isActiveCustomItemCountLimitEnabled) return;
      if (visibleCountServerErrorMessage) {
        setVisibleLimitError(new Error(visibleCountServerErrorMessage));
        return;
      }
      clearVisibleLimitErrorMessage();
    }, [
      clearVisibleLimitErrorMessage,
      isActiveCustomItemCountLimitEnabled,
      setVisibleLimitError,
      visibleCountServerErrorMessage,
    ]);

    const methods = useForm<TransactionItemDetailForm>({
      defaultValues: {
        label: label,
        visibility: visibility,
        is_required: is_required,
        tooltip_message: tooltip_message,
      },
      mode: 'onChange',
    });

    // Custom validation error handler to map between form types
    const handleValidationError = useCallback(
      (field: string, error: ErrorOption) => {
        if (field === 'label') {
          methods.setError('label', error);
        } else if (field === 'is_visible') {
          methods.setError('visibility', error);
        } else if (field === 'tooltip_message') {
          methods.setError('tooltip_message', error);
        } else if (field === 'is_required') {
          methods.setError('is_required', error);
        }
      },
      [methods]
    );

    const { onSubmitUpdate } = useSubmitUpdatePullDownTransactionItem({
      setValidationError: handleValidationError,
      handlerSuccess: (successMsg: string) => {
        clearVisibleLimitBanner();
        notify.success({ title: successMsg, duration: 5 });
        onView();
      },
      handlerError: (errorMsg: string) => {
        notify.error({ title: errorMsg, duration: 5 });
      },
      showLoading: setIsLoading,
      onServerVisibleLimitError: isActiveCustomItemCountLimitEnabled
        ? (msg) => setVisibleCountServerErrorMessage(msg)
        : undefined,
    });

    const labelRules = useMemo(() => {
      const maxLengthValue = 20;
      return {
        required: t('transaction_items.validation_message.required'),
        ...maxLengthWithSurrogate(
          maxLengthValue,
          t('transaction_items.validation_message.maxLength', {
            maxLength: maxLengthValue,
          })
        ),
      };
    }, [t]);

    const tooltipRules = useMemo(() => {
      const maxLengthValue = 100;
      return {
        ...maxLengthWithSurrogate(
          maxLengthValue,
          t('transaction_items.validation_message.maxLength', {
            maxLength: maxLengthValue,
          })
        ),
      };
    }, [t]);

    const handleSubmit: SubmitHandler<TransactionItemDetailForm> = useCallback(
      (data) => {
        onSubmitUpdate({
          label: data.label,
          is_visible: data.visibility,
          is_required: data.is_required,
          tooltip_message: data.tooltip_message,
        });
      },
      [onSubmitUpdate]
    );

    const isVisible = useWatch({
      control: methods.control,
      name: 'visibility',
    });
    const initialVisibility = visibility;

    const prevIsVisibleRef = useRef<boolean | undefined>(undefined);
    useEffect(() => {
      if (prevIsVisibleRef.current === undefined) {
        prevIsVisibleRef.current = isVisible;
        return;
      }
      const isVisibilityUnchecked =
        prevIsVisibleRef.current === true && isVisible === false;
      prevIsVisibleRef.current = isVisible;
      // エラー発生かつチェックボックスのチェックが外れた（true -> false に変化）場合に、VisibleCountのエラーメッセージをクリアする
      // Clear the visible-count error message only when an error exists and the visibility checkbox is unchecked (true -> false).
      if (isVisibilityUnchecked && visibleCountServerErrorMessage) {
        clearVisibleLimitBanner();
      }
    }, [clearVisibleLimitBanner, isVisible, visibleCountServerErrorMessage]);

    const isAtVisibleCustomLimit = useMemo(
      () =>
        visibleCustomItemsInUseCount !== undefined &&
        visibleCustomItemsInUseCount >= MAX_CUSTOM_TRANSACTION_ITEMS_COUNT,
      [visibleCustomItemsInUseCount]
    );

    const showVisibleCountInitialErrorBanner =
      isAtVisibleCustomLimit && !isVisible && !initialVisibility;

    const isUseCheckboxDisabled = showVisibleCountInitialErrorBanner;

    const showVisibleLimitError = Boolean(visibleCountServerErrorMessage);

    const showVisibleLimitWarning =
      !showVisibleLimitError && showVisibleCountInitialErrorBanner;

    const handleCancel = useCallback(() => {
      clearVisibleLimitBanner();
      methods.reset();
      onView();
    }, [clearVisibleLimitBanner, onView, methods]);

    return (
      <FormProvider {...methods}>
        <Form<TransactionItemDetailForm>
          formMethod={methods}
          onSubmit={handleSubmit}
        >
          <div className={cx(styles['container'])}>
            {isActiveCustomItemCountLimitEnabled && showVisibleLimitError && (
              <VisibleLimitErrorProvider value={setVisibleLimitError}>
                <div>{visibleLimitErrorContextHolder}</div>
              </VisibleLimitErrorProvider>
            )}
            <div className={cx(styles['button-container'])}>
              <ButtonV2
                isSecondary
                onClick={handleCancel}
                isDisabled={isLoading}
              >
                {t('cancel')}
              </ButtonV2>
              <ButtonV2
                type='submit'
                isLoading={isLoading}
                isDisabled={
                  isLoading ||
                  (isActiveCustomItemCountLimitEnabled && showVisibleLimitError)
                }
              >
                {t('save')}
              </ButtonV2>
            </div>
            <ListView leftRightRatio={leftRightRatio}>
              <Top>
                <ListViewRequiredLabel fullWidth>
                  {t('transaction_items.table.label')}
                </ListViewRequiredLabel>
                <FormInput
                  name='label'
                  inputId='label'
                  control={methods.control}
                  rules={labelRules}
                />
              </Top>
              <Top>
                <ListViewLabel>
                  {t('transaction_items.table.is_visible')}
                </ListViewLabel>
                <div>
                  <FormCheckbox
                    name='visibility'
                    inputId='visibility'
                    control={methods.control}
                    label={t('transaction_items.table.visible')}
                    disabled={isUseCheckboxDisabled}
                  />
                  {isActiveCustomItemCountLimitEnabled && (
                    <>
                      {showVisibleLimitWarning && (
                        <DescriptionCaption type='alert'>
                          {t(
                            'transaction_items.visibility.active_limit_warning'
                          )}
                        </DescriptionCaption>
                      )}
                      {showVisibleLimitError && (
                        <DescriptionCaption type='error'>
                          {t(
                            'transaction_items.visibility.active_limit_warning'
                          )}
                        </DescriptionCaption>
                      )}
                    </>
                  )}
                </div>
              </Top>
              {isFlexibleValidationEnabled && (
                <Top>
                  <ListViewLabel>
                    {t('transaction_items.drawer.is_required')}
                  </ListViewLabel>
                  <FormCheckbox
                    name='is_required'
                    inputId='is_required'
                    control={methods.control}
                    label={t('transaction_items.drawer.is_required.true')}
                  />
                </Top>
              )}
              <Bottom>
                <ListViewLabel>
                  {t('transaction_items.table.tooltip_message')}
                </ListViewLabel>
                <FormInput
                  name='tooltip_message'
                  inputId='tooltip_message'
                  control={methods.control}
                  rules={tooltipRules}
                />
              </Bottom>
            </ListView>
          </div>
        </Form>
      </FormProvider>
    );
  }
);
EditForm.displayName = 'EditForm';
